import { schemaChargePositionPlace } from '@cc/contrats';
import { Bandeau, Bouton, CaseACocher, Champ, Squelette } from '@cc/ui';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { messageErreur } from '../../api/erreurs';
import { positionContrat, type PositionTerrain } from '../../terrain/appareil';
import { uuidV7 } from '../../terrain/identifiants';
import { placesEmportees, telechargerPlace, type PlaceEmportee } from '../../terrain/places';
import { releverPosition } from '../../terrain/position';
import { synchroniseurPartage } from '../../terrain/synchronisation';
import { useEnLigne, useOperationsLocales, useSynchronisation } from '../../terrain/useTerrain';
import { EnTeteTerrain, Section } from './commun';
import { SaisiePhotos, versPhotosAEnregistrer, type PhotoSaisie } from './SaisiePhotos';
import styles from './terrain.module.css';

export function PagePlace() {
  const { id = '' } = useParams();
  return <FichePlace key={id} id={id} />;
}

function FichePlace({ id }: { id: string }) {
  const [place, setPlace] = useState<PlaceEmportee | null>(null);
  const [chargement, setChargement] = useState(true);
  const [position, setPosition] = useState<PositionTerrain | null>(null);
  const [photos, setPhotos] = useState<PhotoSaisie[]>([]);
  const [confirme, setConfirme] = useState(false);
  const [nom, setNom] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const enLigne = useEnLigne();
  const operations = useOperationsLocales(id);
  const sync = useSynchronisation();
  const attente = (operations.donnees ?? []).some(
    (o) => o.etat === 'en_attente' || o.etat === 'en_cours',
  );
  const conflit = (operations.donnees ?? []).some((o) => o.etat === 'conflit');
  useEffect(() => {
    let actif = true;
    void placesEmportees()
      .then(
        async (places) =>
          places.find((p) => p.id === id) ?? (enLigne ? await telechargerPlace(id) : null),
      )
      .then((p) => {
        if (actif) {
          setPlace(p);
        }
      })
      .catch((e: unknown) => {
        if (actif) setErreur(messageErreur(e));
      })
      .finally(() => {
        if (actif) setChargement(false);
      });
    return () => {
      actif = false;
    };
  }, [id, enLigne, sync.revision]);

  const relever = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      const r = await releverPosition(8000);
      if (!r.position) throw new Error(r.erreur ?? 'Position indisponible');
      setPosition(r.position);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };
  const enregistrer = async () => {
    if (!place || !position || !confirme || attente) return;
    setEnCours(true);
    setErreur(null);
    try {
      const charge = schemaChargePositionPlace.parse({
        position: positionContrat(position),
        photo_id_client: photos[0]?.id_client,
        attribution_id: place.attribution_en_cours?.id ?? null,
        commercant_confirme: true,
        ...(place.attribution_en_cours
          ? { commercant_nom: nom ?? place.attribution_en_cours.commercant }
          : {}),
      });
      await synchroniseurPartage().enfiler(
        {
          id_client: uuidV7(),
          type: 'position_place',
          objet_type: 'emplacement',
          objet_id: place.id,
          version_attendue: place.position_version ?? 0,
          enregistre_le: new Date().toISOString(),
          charge,
        },
        {
          titre: 'Correction de position',
          objet_libelle: place.reference,
          photos: versPhotosAEnregistrer(photos, 'emplacement'),
        },
      );
      setPosition(null);
      setPhotos([]);
      setConfirme(false);
      setMessage(
        'Correction enregistrée sur cet appareil. Son résultat est indiqué dans la synchronisation.',
      );
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };
  const recharger = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      const p = await telechargerPlace(id);
      setPlace(p);
      setNom(p.attribution_en_cours?.commercant ?? '');
      setPosition(null);
      setConfirme(false);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };
  return (
    <>
      <EnTeteTerrain
        titre={place ? `Place ${place.reference}` : 'Place de marché'}
        retour="/terrain"
      />
      {chargement ? <Squelette lignes={4} /> : null}
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      {message ? <Bandeau type="info">{message}</Bandeau> : null}
      {!chargement && !place ? (
        <p>
          Cette place n’est pas disponible sur l’appareil. Ouvrez sa fiche avec une connexion avant
          votre déplacement.
        </p>
      ) : null}
      {place ? (
        <Section titre="Correction sur le terrain">
          <div className={styles.pile}>
            <p>
              {place.attribution_en_cours
                ? `Occupant enregistré : ${place.attribution_en_cours.commercant}`
                : 'Aucune attribution en cours.'}
            </p>
            {conflit ? (
              <Bandeau type="attention">
                Une correction est en conflit. Rechargez la fiche, vérifiez l’occupant et la
                position, puis saisissez une nouvelle correction. L’ancienne reste conservée dans le
                journal.
              </Bandeau>
            ) : null}
            <Bouton
              variante="secondaire"
              disabled={!enLigne || attente || enCours}
              onClick={() => void recharger()}
            >
              Recharger la fiche du serveur
            </Bouton>
            <Bouton disabled={attente || enCours} onClick={() => void relever()}>
              Déplacer à ma position
            </Bouton>
            {position ? (
              <p>
                Position relevée : {position.latitude.toFixed(6)}, {position.longitude.toFixed(6)}.
              </p>
            ) : null}
            <SaisiePhotos
              photos={photos}
              onChange={setPhotos}
              max={1}
              libelle="Photo de la place"
            />
            {place.attribution_en_cours ? (
              <Champ
                libelle="Nom du commerçant"
                value={nom ?? place.attribution_en_cours.commercant}
                onChange={(e) => setNom(e.target.value)}
                maxLength={200}
                required
              />
            ) : null}
            <CaseACocher
              libelle={
                place.attribution_en_cours
                  ? 'Je confirme le commerçant présent sur cette place'
                  : 'Je confirme que la place est libre'
              }
              checked={confirme}
              onChange={(e) => setConfirme(e.target.checked)}
            />
            <Bouton
              disabled={!position || !confirme || attente || enCours}
              onClick={() => void enregistrer()}
            >
              Enregistrer la correction
            </Bouton>
          </div>
        </Section>
      ) : null}
    </>
  );
}
